// Eskom PARCC — the Report-to-Management document model + finding auto-draft (Phase 2).
//
// Shapes the (based, in-scope) transactions + findings for ONE division × quarter
// into the plain-data RtmDocModel the DOCX / PDF renderers lay out — with every
// derived figure carrying its provenance so the composer and the issued document
// can mark a consultant override. Finding auto-draft is TRANSACTION-GRAIN FIRST:
// the outstanding transactions (the same set as Table 1 and the conclusion), each
// with its genuine lines grouped by criterion, one RtmFinding per tier present.
// Pure; tested in test/features/reports/rtm/buildRtmDocModel.test.ts.
import type { Transaction } from '../../../data/types';
import type { FindingRead } from '../../../lib/db';
import { genuineFindings } from '../../../domain/findings';
import { isOutstanding, isSelfDeclared, hasGap, gapTierOf, type GapTier } from '../../../domain/gaps';
import { resolveRtmClass, type RtmClass } from './rtmClass';
import { phraseFor } from './rtmPhrasebook';
import { formatRand } from '../../../lib/money';

export type Provenance = 'system' | 'system:history' | 'override' | 'static';

/** One Table-1 row (per RTM mechanism class). */
export interface RtmTable1Row {
  rtmClass: RtmClass;
  gapsIdentified: number;
  gapsAddressed: number;
  sdGaps: number;
  sdPfma: number;
  sdPolicy: number;
  sdHousekeeping: number;
  totalAddressed: number;
  provenance: Provenance;
  /** history coverage fraction "192 of 230", when derived from CES backups. */
  coverageNote?: string;
}

/** One detailed finding (§7) + its Table-2 short form. */
export interface RtmDocFinding {
  number: string; // '4.1'
  heading: string; // 'FORMAL TENDERING'
  classification: string;
  findingTitle: string;
  rating: number; // 1..4
  tier: GapTier;
  criteria: string;
  observation: string;
  risk: string;
  rootCausePrimary: string;
  rootCauseSecondary: string;
  recommendations: string[];
  linkedRefs: string[];
  summaryObservation: string;
}

export interface RtmDocModel {
  fy: string;
  quarter: string;
  division: string;
  longName: string;
  reportDate: string;
  title: string;
  addressees: { name: string; title: string; org?: string; addressLines?: string[] }[];
  acknowledgers: { name: string; title: string }[];
  signatoryName: string;
  signatoryTitle: string;
  coverLetter: string[];
  populationInitial: number;
  populationFinal: number;
  populationNote: string;
  populationByClass: { rtmClass: RtmClass; count: number }[];
  table1: RtmTable1Row[];
  table1Total: RtmTable1Row;
  findings: RtmDocFinding[];
  keyRecommendations: string[];
  overallRating: number | null;
  conclusionText: string;
  unclosedCount: number;
  ieCount: number;
  ieValue: number;
  managementResponse: string;
}

const TIER_RATING: Record<GapTier, number> = { PFMA: 3, 'Policy & Procedures': 2, Housekeeping: 1 };
const TIER_TITLE: Record<GapTier, string> = {
  PFMA: 'Non-Compliance to PFMA / Potential IE (Quantitative)',
  'Policy & Procedures': 'Non-conformance to Policy & Procedures',
  Housekeeping: 'Housekeeping',
};

function ieValueOf(t: Transaction): number {
  return gapTierOf(t.complianceLevel) === 'PFMA' ? (t.value ?? 0) : 0;
}

export interface RtmDraftInput {
  fy: string;
  quarter: string;
  division: string;
  longName: string;
  reportDate: string;
  /** In-scope, based (effective-level) rows for this division × quarter. */
  rows: readonly Transaction[];
  findings: readonly FindingRead[];
  /** History everGap per ref (from CES backups) + coverage, when available. */
  everGapRefs?: ReadonlySet<string>;
  everGapCoverage?: { covered: number; total: number };
  classOverrides?: Record<string, string>;
  classMap?: Record<string, string>;
  scopeLimitationRefs?: readonly string[];
}

/** Auto-draft the full RtmDocModel from the numbers + the phrasebook. */
export function buildRtmDocModel(input: RtmDraftInput): RtmDocModel {
  const { rows } = input;
  const classOf = (t: Transaction): RtmClass => resolveRtmClass(t, input.classOverrides, input.classMap);
  const refs = new Set(rows.map((t) => t.referenceNo));
  const lines = genuineFindings(input.findings).filter((f) => f.referenceNo && refs.has(f.referenceNo));
  const linesByRef = new Map<string, FindingRead[]>();
  for (const f of lines) {
    const k = f.referenceNo ?? '';
    (linesByRef.get(k) ?? linesByRef.set(k, []).get(k)!).push(f);
  }

  // ---- Table 1 (per class): identified / addressed / self-declared tiers ----
  const everGap = (t: Transaction): boolean =>
    (input.everGapRefs?.has(t.referenceNo) ?? false) || hasGap(t) || linesByRef.has(t.referenceNo);
  const classes = new Map<RtmClass, Transaction[]>();
  for (const t of rows) {
    const c = classOf(t);
    (classes.get(c) ?? classes.set(c, []).get(c)!).push(t);
  }
  const tierOf = (t: Transaction): GapTier | null => gapTierOf(t.complianceLevel);
  const buildT1 = (rtmClass: RtmClass, group: readonly Transaction[]): RtmTable1Row => {
    const identified = group.filter(everGap);
    const sd = group.filter(isSelfDeclared);
    const addressed = identified.filter((t) => !isOutstanding(t) && !isSelfDeclared(t));
    const sdTier = (tier: GapTier): number => sd.filter((t) => tierOf(t) === tier).length;
    return {
      rtmClass,
      gapsIdentified: identified.length,
      gapsAddressed: addressed.length,
      sdGaps: sd.length,
      sdPfma: sdTier('PFMA'),
      sdPolicy: sdTier('Policy & Procedures'),
      sdHousekeeping: sdTier('Housekeeping'),
      totalAddressed: addressed.length + sd.length,
      provenance: input.everGapRefs ? 'system:history' : 'system',
      coverageNote:
        input.everGapCoverage && input.everGapCoverage.covered < input.everGapCoverage.total
          ? `${input.everGapCoverage.covered} of ${input.everGapCoverage.total} with history`
          : undefined,
    };
  };
  const table1 = [...classes.entries()]
    .map(([c, g]) => buildT1(c, g))
    .filter((r) => r.gapsIdentified > 0 || r.sdGaps > 0)
    .sort((a, b) => b.gapsIdentified - a.gapsIdentified);
  const table1Total = buildT1('Other', rows);
  table1Total.rtmClass = 'Other';

  const populationByClass = [...classes.entries()]
    .map(([rtmClass, g]) => ({ rtmClass, count: g.length }))
    .sort((a, b) => b.count - a.count);

  // ---- Detailed findings — transaction grain first, one per tier present ----
  const outTx = rows.filter(isOutstanding);
  const byTier = new Map<GapTier, Transaction[]>();
  for (const t of outTx) {
    const tier = tierOf(t) ?? 'Policy & Procedures';
    (byTier.get(tier) ?? byTier.set(tier, []).get(tier)!).push(t);
  }
  const tierOrder: GapTier[] = ['PFMA', 'Policy & Procedures', 'Housekeeping'];
  const dominantClasses = (txs: readonly Transaction[]): string => {
    const counts = new Map<RtmClass, number>();
    for (const t of txs) counts.set(classOf(t), (counts.get(classOf(t)) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2).map(([c]) => c).join(' and ') || 'Procurement';
  };
  const findings: RtmDocFinding[] = [];
  let n = 0;
  for (const tier of tierOrder) {
    const txs = byTier.get(tier);
    if (!txs || txs.length === 0) continue;
    n += 1;
    const heading = dominantClasses(txs).toUpperCase();
    // Cluster the tier's lines by criterion (top clusters first).
    const clusterCounts = new Map<string, { count: number; refs: Set<string> }>();
    for (const t of txs) {
      for (const f of linesByRef.get(t.referenceNo) ?? []) {
        const crit = String(f.criterion ?? f.text ?? '').trim();
        if (!crit) continue;
        const c = clusterCounts.get(crit) ?? { count: 0, refs: new Set() };
        c.count += 1;
        c.refs.add(t.referenceNo);
        clusterCounts.set(crit, c);
      }
    }
    const topClusters = [...clusterCounts.entries()].sort((a, b) => b[1].count - a[1].count).slice(0, 8);
    const primaryCrit = topClusters[0]?.[0] ?? '';
    const phrase = phraseFor(primaryCrit);
    const refsList = txs.map((t) => t.referenceNo);
    const observationBullets = topClusters.map(([crit, c]) => `• ${crit} (${c.refs.size} transaction(s))`).join('\n');
    const observation = topClusters.length
      ? `During our review of the ${dominantClasses(txs)} files for ${refsList.join(', ')} the following was noted:\n${observationBullets}`
      : `During our review of the ${dominantClasses(txs)} files for ${refsList.join(', ')}, non-compliance was recorded by reviewer override — criteria to be entered.`;
    const ieVal = txs.reduce((s, t) => s + ieValueOf(t), 0);
    findings.push({
      number: `4.${n}`,
      heading,
      classification: `${dominantClasses(txs)}`,
      findingTitle: TIER_TITLE[tier],
      rating: TIER_RATING[tier],
      tier,
      criteria: phrase.criteria,
      observation,
      risk: tier === 'PFMA' && ieVal > 0 ? `${phrase.risk} The quantitative effect of the potential irregular expenditure identified has been quantified as ${formatRand(ieVal)}.` : phrase.risk,
      rootCausePrimary: phrase.rootCausePrimary,
      rootCauseSecondary: phrase.rootCauseSecondary,
      recommendations: [phrase.recommendation],
      linkedRefs: refsList,
      summaryObservation: `${TIER_TITLE[tier]} — ${refsList.length} transaction(s): ${refsList.join(', ')}.`,
    });
  }

  // Scope-limitation sub-observation (her 4.1.2), appended to the PFMA finding if present.
  const scopeRefs = (input.scopeLimitationRefs ?? []).filter((r) => refs.has(r));
  if (scopeRefs.length && findings.length) {
    const target = findings.find((f) => f.tier === 'PFMA') ?? findings[0];
    target.observation += `\n\nDocumentation not available for review (Possible Limitation of Scope): ${scopeRefs.join(', ')}. As a result, audit could not verify the completeness, accuracy, validity or compliance of the associated transactions.`;
  }

  const unclosedCount = outTx.length;
  const ieTx = outTx.filter((t) => tierOf(t) === 'PFMA');
  const ieValue = ieTx.reduce((s, t) => s + ieValueOf(t), 0);
  const conclusionText =
    `The Audit Readiness Support engagement remains an ongoing process, with buyers and business representatives ` +
    `continuing to collaborate with the Audit Readiness team to address identified procurement gaps. Notwithstanding ` +
    `this progress, ${unclosedCount} transaction(s) remain unclosed with ${ieTx.length} potential IE, valued at ` +
    `${formatRand(ieValue) || 'R 0'}. The control deficiencies compromise the principles of fairness, transparency, ` +
    `competitiveness and cost-effectiveness as prescribed by Section 217 of the Constitution and the PFMA.`;

  return {
    fy: input.fy,
    quarter: input.quarter,
    division: input.division,
    longName: input.longName,
    reportDate: input.reportDate,
    title: `${input.fy} ${input.quarter} Audit Readiness Support: Report to Management – ${input.longName}`,
    addressees: [],
    acknowledgers: [],
    signatoryName: 'Mr. Justin Kettle',
    signatoryTitle: 'Managing Director',
    coverLetter: [
      `In accordance with Eskom’s request and task order dated 3 February 2025, Kettle Consulting have concluded the compliance reviews for ${input.quarter} ${input.fy} for ${input.longName}.`,
      'Our results are based on the reviews undertaken through consultation with buyers and divisional management during the project. This report is intended to describe the work performed, issues observed in the process, and recommendations for improvement.',
      'We trust the report proves insightful and supports Eskom to improve the audit outcomes in the annual financial reporting process.',
    ],
    populationInitial: rows.length,
    populationFinal: rows.length,
    populationNote: '',
    populationByClass,
    table1,
    table1Total,
    findings,
    keyRecommendations: [],
    overallRating: null,
    conclusionText,
    unclosedCount,
    ieCount: ieTx.length,
    ieValue,
    managementResponse: '',
  };
}
